/**
 * One answer silences every "did you apply?" surface for that job.
 *
 * THE PROBLEM (observed in prod, 2026-09-03). `/en/jobs` rendered BOTH the
 * tab-return toast (`ConfirmApplyToast`, mounted globally in the apex locale
 * layout) and the in-feed followup banner (`ClickedJobFollowupPrompt`) at once,
 * asking the same question about the same job — down to two buttons both
 * labelled "Not yet". A single authenticated outbound click on `/out/[jobId]`
 * triggers two unrelated writers under two unrelated flags:
 * `apply-confirm-loop-v2` sets the `ld_pending_hop` cookie, and
 * `retention-apply-autotrack` inserts the `clicked` tracker row. Nothing
 * coordinated them.
 *
 * Answering one DOES eventually clear the other — the toast's authed branch
 * transitions the very `application_tracker` row that feeds the banner's query
 * — but only on the NEXT server render. Within the render that already showed
 * both, the visitor is asked twice.
 *
 * WHY `jobId` IS THE KEY. It is the only identifier all three confirm surfaces
 * carry. `trackerId` exists on the two tracker-backed banners but not on the
 * toast, whose entire state is a `{ jobId, ts }` cookie. Keying on the job also
 * matches what the visitor experienced: they applied to a job once, so they
 * should be asked about it once.
 *
 * WHY `sessionStorage`. It matches the existing per-surface dismissal these
 * components already use (`offsite-confirm-dismissed-<trackerId>`,
 * `clicked-followup-dismissed-<trackerId>`), and the duplication only ever
 * happens within a single page/session. It does NOT need to cross origins: the
 * two co-render hazards each live entirely inside one deployable (apex
 * `/jobs`, and the `apps/app` zone's `/applications`).
 *
 * FAIL-OPEN, DELIBERATELY. Every read is wrapped and returns `false` when
 * storage throws (private mode, blocked site data). A suppression bug must
 * never hide the only outcome-capture surface this product has — being asked
 * twice is a papercut, being asked zero times loses the answer, and WCA counts
 * only answers.
 */

import { removePendingApply } from "@/lib/apply/pending-apply-store";

const KEY_PREFIX = "apply-confirm-suppressed";

/** Same-tab notification; see `suppressConfirmForJob` for why storage alone fails. */
const CONFIRM_SUPPRESSED_EVENT = "ld:apply-confirm-suppressed";

function suppressionKey(jobId: string): string {
	return `${KEY_PREFIX}:${jobId}`;
}

/**
 * True when some confirm surface already got an answer (or a dismissal) for
 * this job in this session. Returns `false` on any storage error — see the
 * fail-open note above.
 */
export function isConfirmSuppressedForJob(jobId: string): boolean {
	if (!jobId) return false;
	try {
		return window.sessionStorage.getItem(suppressionKey(jobId)) === "1";
	} catch {
		return false;
	}
}

/**
 * Record that this job's confirm question has been answered or dismissed, so
 * the sibling surfaces stop asking it. Call on ANY terminal outcome — an
 * "applied", a "not yet", or an explicit dismiss — because from the visitor's
 * side all three mean "I have dealt with this question".
 *
 * Best-effort: a failed write only means the other surface asks again, which is
 * the behaviour that existed before this module.
 */
export function suppressConfirmForJob(jobId: string, sourceId?: string): void {
	if (!jobId) return;
	try {
		window.sessionStorage.setItem(suppressionKey(jobId), "1");
	} catch {
		// Storage unavailable — the in-memory state of the surface that was just
		// answered still holds for this render, and the event below still hides
		// the siblings for the rest of this page view.
	}
	// The answer also settles the next-visit memory: a job answered on any
	// surface today must not be asked about again tomorrow.
	removePendingApply(jobId);
	// Storage alone is not enough: the `storage` event does NOT fire in the tab
	// that wrote it, and the sibling surface is already mounted, so it would
	// keep asking until the next mount or tab-return. This is the same-tab
	// signal that makes the suppression immediate — which is the whole point,
	// since both prompts are on screen together.
	try {
		window.dispatchEvent(
			new CustomEvent(CONFIRM_SUPPRESSED_EVENT, {
				detail: { jobId, sourceId },
			}),
		);
	} catch {
		// Non-DOM environment; storage still carries the state to the next mount.
	}
}

/**
 * Subscribe to suppression of ONE job, for a surface that is already on screen.
 * Returns an unsubscribe function shaped for a `useEffect` cleanup.
 *
 * Pass the same `sourceId` here and to `suppressConfirmForJob` so a surface
 * ignores its own suppression — see the note in the listener.
 */
export function onConfirmSuppressed(
	jobId: string,
	handler: () => void,
	sourceId?: string,
): () => void {
	const listener = (event: Event) => {
		const detail = (
			event as CustomEvent<{
				jobId?: string;
				sourceId?: string;
			}>
		).detail;
		if (detail?.jobId !== jobId) return;
		// Never suppress the surface that just answered. It owns the post-answer
		// states (the signup nudge, the email capture, the first-apply beat) and
		// hiding it mid-flow would drop the very panel the answer opened — which
		// is exactly what happened when this event carried no source.
		if (sourceId !== undefined && detail?.sourceId === sourceId) return;
		handler();
	};
	try {
		window.addEventListener(CONFIRM_SUPPRESSED_EVENT, listener);
	} catch {
		return () => {};
	}
	return () => window.removeEventListener(CONFIRM_SUPPRESSED_EVENT, listener);
}

/**
 * One prompt per job at a time (seeker funnel RCA, 2026-10-14).
 *
 * Suppression above only kicks in once somebody ANSWERS. Before that, the
 * tab-return toast and the in-page ask (`apply-inpage-confirm-v1`) both arm
 * off the same apply click and both reveal on the same tab return — on mobile
 * job detail the toast (`fixed`, z-50) then painted on top of the in-page ask
 * rendered inside the fixed apply footer. PostHog 14d: in-page 50–264 shown →
 * 0–3 answered; replays show seekers re-tapping "Lamar sekarang" instead.
 *
 * A claim is held by a surface KIND ("toast" | "inpage"), not an instance, so
 * the two in-page copies the job page mounts (sidebar + footer) share one
 * claim. In-memory only: both surfaces live in the same page view, and a
 * claim must never outlive it (fail-open — a stale claim would hide the only
 * outcome-capture surface).
 */
export type ConfirmSurfaceKind = "toast" | "inpage";

const CONFIRM_CLAIM_EVENT = "ld:apply-confirm-claim";
const claims = new Map<string, ConfirmSurfaceKind>();

/** Claim the job's confirm slot for `kind`. Returns false when the other kind
 * already holds it (the caller must then stay hidden). */
export function claimConfirmForJob(
	jobId: string,
	kind: ConfirmSurfaceKind,
): boolean {
	if (!jobId) return true;
	const holder = claims.get(jobId);
	if (holder && holder !== kind) return false;
	if (!holder) {
		claims.set(jobId, kind);
		notifyClaimChange(jobId);
	}
	return true;
}

/** Release `kind`'s claim on the job (no-op when another kind holds it). */
export function releaseConfirmForJob(
	jobId: string,
	kind: ConfirmSurfaceKind,
): void {
	if (claims.get(jobId) !== kind) return;
	claims.delete(jobId);
	notifyClaimChange(jobId);
}

/** True when a surface of a DIFFERENT kind currently holds the job's slot. */
export function isConfirmClaimedByOther(
	jobId: string,
	kind: ConfirmSurfaceKind,
): boolean {
	const holder = claims.get(jobId);
	return holder !== undefined && holder !== kind;
}

/** Subscribe to claim changes (any job). Returns an unsubscribe function. */
export function onConfirmClaimChange(handler: (jobId: string) => void) {
	const listener = (event: Event) => {
		const jobId = (event as CustomEvent<{ jobId?: string }>).detail?.jobId;
		if (jobId) handler(jobId);
	};
	try {
		window.addEventListener(CONFIRM_CLAIM_EVENT, listener);
	} catch {
		return () => {};
	}
	return () => window.removeEventListener(CONFIRM_CLAIM_EVENT, listener);
}

function notifyClaimChange(jobId: string): void {
	try {
		window.dispatchEvent(
			new CustomEvent(CONFIRM_CLAIM_EVENT, { detail: { jobId } }),
		);
	} catch {
		// Non-DOM environment.
	}
}

/** Exported for tests: the claim map outlives a test. */
export function resetConfirmClaims(): void {
	claims.clear();
}
